import React from 'react';
import type { ToolFilterCondition } from '@troia-v3/types';

import { Icon } from '@/shared/icons';

import { createEmptyWebhookFilter } from './WebhookFilterDefinitions';
import { WebhookFilterRow } from './WebhookFilterRow';

interface WebhookFilterBuilderProps {
  filters: ToolFilterCondition[];
  onChange: (filters: ToolFilterCondition[]) => void;
  maxFilters?: number;
  disabled?: boolean;
}

export const WebhookFilterBuilder: React.FC<WebhookFilterBuilderProps> = ({
  filters,
  onChange,
  maxFilters = 10,
  disabled,
}) => {
  const handleAdd = (): void => {
    if (filters.length >= maxFilters) return;
    onChange([...filters, createEmptyWebhookFilter()]);
  };

  const handleUpdate = (index: number, updated: ToolFilterCondition): void => {
    const next = [...filters];
    next[index] = updated;
    onChange(next);
  };

  const handleRemove = (index: number): void => {
    onChange(filters.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3" data-testid="webhook-filter-builder">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Todos os filtros devem ser atendidos (lógica E). Requisições reprovadas são
          descartadas com registro "Filtrado" no log.
        </p>
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled || filters.length >= maxFilters}
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50"
          style={{ color: 'var(--theme-primary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          data-testid="webhook-filter-add"
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {filters.length === 0 && (
        <div
          className="p-6 text-center rounded-lg border-2 border-dashed"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <Icon name="filter" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)', opacity: 0.4 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum filtro configurado — todas as requisições serão processadas.
          </p>
        </div>
      )}

      {filters.map((filter, index) => (
        <WebhookFilterRow
          key={index}
          filter={filter}
          index={index}
          onChange={(updated) => handleUpdate(index, updated)}
          onRemove={() => handleRemove(index)}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

WebhookFilterBuilder.displayName = 'WebhookFilterBuilder';
